export type SnowflakeAuthMethod = 'password' | 'keyPair';

// More specific table names, allowing for optional tables
export interface SnowflakeTableNames {
  event?: string;
  contact_update?: string;
  account_update?: string;
}

// More specific column names, allowing for optional columns and tables
// Using Record<string, string | undefined> to represent the mapping from MadKudu's standard field names
// to the customer's column names in Snowflake.
export interface SnowflakeEventColumnNames
  extends Record<string, string | undefined> {
  id?: string;
  event_name?: string;
  event_timestamp?: string;
  contact_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SnowflakeContactColumnNames
  extends Record<string, string | undefined> {
  id?: string;
  email?: string;
  account_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SnowflakeAccountColumnNames
  extends Record<string, string | undefined> {
  id?: string;
  domain?: string;
  salesforce_account_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SnowflakeColumnNames {
  event?: SnowflakeEventColumnNames;
  contact_update?: SnowflakeContactColumnNames;
  account_update?: SnowflakeAccountColumnNames;
}
export interface SnowflakeCredentialsBase {
  account: string;
  username: string;
  database: string;
  schema: string;
  tableNames: SnowflakeTableNames; // Use the more specific type, allow for undefined if a table is not mapped
  columnNames?: SnowflakeColumnNames; // Use the more specific type, allow for undefined
  authMethod: SnowflakeAuthMethod;
}

export interface PasswordCredentials extends SnowflakeCredentialsBase {
  authMethod: 'password';
  pwd: string;
}

export interface KeyPairCredentials extends SnowflakeCredentialsBase {
  authMethod: 'keyPair';
  privateKey: string;
  privateKeyPass?: string;
}

export type SnowflakeCredentialsType = PasswordCredentials | KeyPairCredentials;

export function isKeyPairCredentials(
  credentials: SnowflakeCredentialsType | null | undefined
): credentials is KeyPairCredentials {
  return credentials?.authMethod === 'keyPair';
}

export function isPasswordCredentials(
  credentials: SnowflakeCredentialsType | null | undefined
): credentials is PasswordCredentials {
  return credentials?.authMethod === 'password';
}

// Keep existing utility types if they are still relevant or adapt them
// For example, Status and SnowflakeState might need to be updated to use SnowflakeCredentialsType

// Loading status
export type Status = 'idle' | 'loading' | 'error' | 'success';

// State
export type SnowflakeState = {
  tenant: number;
  credentials?: SnowflakeCredentialsType; // Updated to use the new union type
  previousCredentials?: SnowflakeCredentialsType; // Updated
  status: Status;
  error?: Error;
};

// Methods
export type SnowflakeMethods = {
  // Returns true if credentials were loaded from the server
  isLoaded: () => boolean;
  isDirty: () => boolean;
  isAuthenticated: () => boolean;
  isValid: () => boolean;

  // These methods perform some actions on the server and, eventually, update the state
  loadCredentials: () => Promise<void>;
  saveCredentials: () => Promise<void>;
  revokeCredentials: () => Promise<void>;
  setCredentials: (credentials: SnowflakeCredentialsType) => void; // Updated
};
